import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import React, { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { twMerge } from 'tailwind-merge'
import { setWorkspace } from 'shared/api/workspace-change-api'
import useUser, { isAuthorizedUser, isWorkspaceOwner } from 'shared/hooks/use-user'
import ChevronIcon from 'shared/icons/chevron-icon'
import { useLogoUrl } from './hooks/use-logo-url'

interface WorkspaceSelectorProps {
  className?: string
  position: 'top-menu' | 'side-menu'
}
interface WorkspaceInterface {
  id: number | null
  isWorkspace: boolean
  name: string
  avatarUrl: string
}
export function WorkspaceSelector({ position, className }: WorkspaceSelectorProps) {
  const { user, mutate, isOwner } = useUser()
  const [selectedWorkspace, setSelectedWorkspace] = useState<WorkspaceInterface>()
  const [workspaces, setWorkspaces] = useState<WorkspaceInterface[]>([])
  const [isFetching, setIsFetching] = useState(false)
  const { pathname } = useRouter()
  const router = useRouter()
  const redirectUrl = useLogoUrl()
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (isAuthorizedUser(user) && user.workspaceMemberships.length !== 0) {
      setWorkspaces([
        ...user.workspaceMemberships.map(el => ({
          isWorkspace: el.id === selectedWorkspace?.id,
          id: el.id,
          name: el.name,
          avatarUrl: el.image,
        })),
        {
          id: user.workspaceMemberships[0].ownerId,
          isWorkspace: false,
          name: user.subdomain,
          avatarUrl: user.avatarUrl,
        },
      ])
    }
  }, [user, isOwner])

  useEffect(() => {
    if (user && isAuthorizedUser(user) && workspaces.length !== 0) {
      user.workspaceOwner
        ? setSelectedWorkspace(prev => {
            const el = user.workspaceMemberships.find(el => el.ownerId === user.workspaceOwner)
            if (el) {
              return {
                name: el.name,
                isWorkspace: true,
                id: el.id,
                avatarUrl: el.image,
              }
            }
          })
        : setSelectedWorkspace({
            name: user.subdomain,
            isWorkspace: false,
            id: user.workspaceMemberships[0].ownerId,
            avatarUrl: user.avatarUrl,
          })
    }
  }, [workspaces])

  const onWorkspaceChange = async (workspace: WorkspaceInterface) => {
    try {
      setIsFetching(true)

      if (user && isAuthorizedUser(user) && redirectUrl) {
        const isOwner = workspace.id === user.workspaceMemberships[0].ownerId
        await setWorkspace({ currentWorkspaceMembership: isOwner ? null : workspace.id })
        await mutate()

        if (pathname !== redirectUrl) {
          router.push(redirectUrl)
        }

        router.reload()
      }
    } finally {
      setIsFetching(false)
    }
  }

  return selectedWorkspace ? (
    <div className={`${position === 'top-menu' && 'hidden lg:flex'} ${className || ''}`}>
      <Menu>
        <div>
          <MenuButton
            onClick={() => {
              if (dropdownRef.current && buttonRef.current) {
                const buttonWidth = buttonRef.current.getBoundingClientRect().width
                const dropdownWidth = dropdownRef.current.getBoundingClientRect().width
                if (dropdownWidth < buttonWidth) {
                  dropdownRef.current.style.width = `${buttonWidth}px`
                }
              }
            }}
            className={twMerge(
              'relative flex w-full items-center justify-between gap-3 rounded-md border border-text-secondary/30 bg-transparent px-4 py-2 text-sm font-medium',
              position === 'top-menu' ? 'text-text-secondary' : 'text-text-primary',
            )}
            as={'button'}
            ref={buttonRef}
            disabled={isFetching}
          >
            {position === 'side-menu' && (
              <img
                className="size-7 rounded-full object-cover"
                src={selectedWorkspace.avatarUrl}
                alt={`${selectedWorkspace.name}`}
              />
            )}
            <span className="flex-1 text-start"> {selectedWorkspace.name}</span>
            <ChevronIcon
              className={twMerge('size-3', position === 'side-menu' && 'rotate-180')}
              aria-hidden="true"
            />
          </MenuButton>
        </div>
        <Transition
          as={'div'}
          className="relative"
          enter="transition duration-300 ease-out"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition duration-175 ease-in"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <MenuItems
            portal={position === 'side-menu'}
            anchor={{ to: position === 'top-menu' ? 'bottom start' : 'top start', gap: 10 }}
            className={twMerge(
              'divide-y divide-text-secondary/30 rounded-md bg-background-secondary shadow-lg ring-1 ring-black/5 focus:outline-none',
              position === 'top-menu' && 'w-max',
            )}
          >
            <div ref={dropdownRef} className="p-1">
              {workspaces.map(el => {
                if (el.id === selectedWorkspace.id) return null
                return (
                  <MenuItem key={el.id}>
                    {({ focus }) => (
                      <button
                        onClick={() => onWorkspaceChange(el)}
                        className={twMerge(
                          'group relative flex w-full gap-3',
                          focus
                            ? 'bg-brand-primary text-background-secondary'
                            : 'text-text-primary',
                          'items-center rounded-md px-3 py-1 text-sm',
                        )}
                      >
                        <img
                          className="size-7 rounded-full object-cover"
                          src={el.avatarUrl}
                          alt={`${el.name}`}
                        />
                        {el.name}
                      </button>
                    )}
                  </MenuItem>
                )
              })}
            </div>
          </MenuItems>
        </Transition>
      </Menu>
    </div>
  ) : null
}
